import { DateHelper } from "@eljouren/utils";
import { classNames, usePrevious } from "@ipis/client-essentials";
import { useEffect, useMemo } from "react";
import { Controller, FieldErrors, UseFormReturn } from "react-hook-form";
import GoogleAddressInput from "../../../../components/GoogleAdressInput";
import { AppButton } from "../../../../components/common/buttons/AppButton";
import { AppFormSelect } from "../../../../components/common/select/AppFormSelect";
import { AppFormTextField } from "../../../../components/common/text-fields/AppFormTextField";
import { useLoading } from "../../../../hooks/hooks";
import { useApiClients } from "../../../../hooks/use-api-clients";
import ClientUtils from "../../../../utils/ClientUtils";
import { BookingSuggestionsResult } from "../SalesTeamBookingSuggestionsRoute";
import {
  useBookingSuggestionDates,
  UseBookingSuggestionDatesOutput,
  useHandymanQualifications,
} from "./booking-suggestions-form-hooks";
import {
  BookingSuggestionsFormValues,
  WeekDayTupleType,
} from "./booking-suggestions-form-schemas";
import { useBookingSuggestionsForm } from "./use-booking-suggestions-form";
import MyLink from "../../../../components/routing/MyLink";
import AppRoutes from "../../../../routes";

type ChildComponentProps = {
  form: UseFormReturn<BookingSuggestionsFormValues>;
  dates: UseBookingSuggestionDatesOutput;
};

interface Props {
  className?: string;
  onBookingSuggestionsReturned: (results: BookingSuggestionsResult) => void;
  country: "Sweden" | "Norway"
}

const SalesTeamBookingSuggestionsForm = (props: Props) => {
  const _qualifications = useHandymanQualifications();

  const qualifications = useMemo(() => {

    if (props.country === "Norway") {
      return _qualifications.filter(q => q.name.startsWith("NO - "));
    }

    return _qualifications.filter(q => !q.name.startsWith("NO - "));


  }, [_qualifications, props.country])

  const form = useBookingSuggestionsForm({
    qualifications,
  });

  const selectedWeekNumber = Number(form.watch("week"));

  const selectedQualificationName = form.watch("qualification");

  const selectedQualification = qualifications.find(
    (q) => q.name === selectedQualificationName
  );

  const dates = useBookingSuggestionDates({
    form,
    selectedQualification,
    selectedWeekNumber,
  });

  const isOnDuty = !!selectedQualification?.onDuty;

  const previousQualification = usePrevious(selectedQualification);

  useEffect(() => {
    if (!selectedQualification || !selectedQualification.onDuty) {
      return;
    }

    if (previousQualification?.onDuty) {
      return;
    }

    const firstAvailableWeek = dates.selectableWeeks.at(0);

    if (!firstAvailableWeek) {
      return;
    }

    if (firstAvailableWeek.number === selectedWeekNumber) {
      return;
    }

    /* 
      When the user selects an on duty qualification for the first time after having
      selected a non on duty qualification, reset the selected week to the first.
    */

    form.setValue("week", firstAvailableWeek.number);
  }, [
    selectedQualification,
    dates.selectableWeeks,
    previousQualification?.onDuty,
    form,
    selectedWeekNumber,
  ]);

  //const days = dates.days;
  useEffect(() => {
    /* 
      When the qualification.onDuty changes, auto select new dates
    */
    const selectedDays: WeekDayTupleType = dates.days.map((dayObj) => {
      const helper = new DateHelper(dayObj.date);
      if (selectedQualification?.onDuty) {
        /* 
            Select only today if the qualification is of type "onDuty"
          */
        return helper.isToday;
      } else {
        /* 
            Select every non weekend date from today and in the future
          */
        return !helper.isWeekend && dayObj.selectable;
      }
    }) as WeekDayTupleType;

    form.setValue("weekDays", selectedDays);
  }, [selectedQualification, dates.selectedWeek, dates.days, form]);

  const { bookingSuggestionsApiClient: apiClient } = useApiClients();
  const loader = useLoading();

  async function onSubmit(values: BookingSuggestionsFormValues) {
    try {
      if (values.type === "simplified") {
        return await onSimplifiedSubmit(values);
      }

      return await onOriginalSubmit(values);
    } catch (er) {
      console.log(er);
      window.modal.alert({
        title: "Okänt fel vid sökning",
        prompt: "Vänligen försök igen senare.",
        typeOfAlert: "error",
        error: er,
      });
    }
  }

  async function onOriginalSubmit(
    values: Extract<BookingSuggestionsFormValues, { type: "original" }>
  ) {
    const helper = new DateHelper();
    const currentWeek = helper.week;
    const selectedWeek = values.week;

    let year: number = helper.year;
    if (selectedWeek < currentWeek) {
      year++;
    }

    const suggestions = await loader.loadWhilePromise(
      apiClient.findWorkOrderBookingSuggestions({
        ...values,
        year,
        isOnDuty,
      })
    );

    props.onBookingSuggestionsReturned({
      type: "original",
      suggestions,
    });
  }

  async function onSimplifiedSubmit(
    values: Extract<BookingSuggestionsFormValues, { type: "simplified" }>
  ) {
    const suggestions = await loader.loadWhilePromise(
      apiClient.findSimplifiedWorkOrderBookingSuggestions({
        ...values,
        isOnDuty,
      })
    );

    props.onBookingSuggestionsReturned({
      type: "simplified",
      suggestions,
    });
  }

  function onError(errors: FieldErrors<BookingSuggestionsFormValues>) {
    if (errors.address) {
      window.modal.alert({
        typeOfAlert: "error",
        title: "Välj adress",
        prompt: "Vänligen välj en adress.",
      });
    }
    console.log(errors);
  }

  return (
    <form
      className={ClientUtils.twClassNames(
        "relative mb-auto flex flex-col gap-4 transition-opacity",
        loader.isLoading && "pointer-events-none opacity-50",
        props.className
      )}
      onSubmit={form.handleSubmit(onSubmit, onError)}
    >
      <LocationSearch form={form} dates={dates} />
      <div className="flex flex-col gap-1">
        <AppFormSelect
          register={form.register}
          name="qualification"
          label="Kompetens"
          options={qualifications.map((q) => ({
            label: q.name,
            value: q.name,
          }))}
        />
        {props.country === "Sweden" && <div className="flex items-center text-sm gap-1 text-faded-gray">
          <span>Visar svenska kompetenser.</span>
          <MyLink
            to={AppRoutes.salesTeam.bookingSuggestionsWithCountry("norway")}
            className="underline"
          >
            Gå till sidan för Norge.
          </MyLink>
        </div>}

        {props.country === "Norway" && <div className="flex items-center text-sm gap-1 text-faded-gray">
          <span>Visar norska kompetenser.</span>
          <MyLink
            to={AppRoutes.salesTeam.bookingSuggestionsWithCountry("sweden")}
            className="underline"
          >
            Gå till sidan för Sverige.
          </MyLink>
        </div>}
      </div>

      <TypeOfSuggestionsTabs form={form} dates={dates} />

      <AppButton
        className="mt-8 w-full"
        type="submit"
        disabled={loader.isLoading}
      >
        Hitta bokningsförslag
      </AppButton>
    </form>
  );
};

/* 
  Do they want to search using the original flow that takes schedule, time and other work orders into account?
  Or do they want to use the simpler version which only takes distance from the company address into account?
*/
function TypeOfSuggestionsTabs(props: ChildComponentProps) {
  const type = props.form.watch("type");

  return (
    <section className="flex flex-col gap-4 pt-8">
      <header className="grid grid-cols-[auto,auto,minmax(0,1fr)] grid-rows-1 gap-4 border-b pb-1">
        <button
          type="button"
          onClick={() => props.form.setValue("type", "simplified")}
          className={classNames(
            "hover:text-gray-900 hover:underline",
            type !== "simplified" && "text-gray-500",
            type === "simplified" && "text-gray-900 underline"
          )}
        >
          Förenklat flöde
        </button>
        <div className="flex h-full w-[1px] bg-gray-200" />
        <button
          type="button"
          onClick={() => props.form.setValue("type", "original")}
          className={classNames(
            "hover:text-gray-900 hover:underline mr-auto ",
            type !== "original" && "text-gray-500",
            type === "original" && "text-gray-900 underline"
          )}
        >
          Original
        </button>


      </header>
      <div>
        <DynamicFields {...props} />
      </div>
    </section>
  );
}

function DynamicFields(props: ChildComponentProps) {
  const type = props.form.watch("type");

  if (type === "simplified") {
    return (
      <div className="flex flex-col gap-4">
        <p className="max-w-prose text-sm">
          Tar enbart hänsyn till distans och restid, utgått från adressen för
          hantverkarns företag.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="max-w-prose text-sm">
        Tar hänsyn till hantverkarens tillgänglighet, andra arbetsordrar och
        datum.
      </p>

      <DurationFieldset {...props} />

      <AppFormSelect
        register={props.form.register}
        name="week"
        label="Vecka"
        options={props.dates.selectableWeeks.map((w) => ({
          value: w.number,
          label: w.label,
        }))}
      />

      <DaySelectFieldset {...props} />
    </div>
  );
}

function LocationSearch(props: ChildComponentProps) {
  const address = props.form.watch("address")?.name;

  return (
    <Controller
      control={props.form.control}
      name="address"
      render={(renderProps) => {
        return (
          <div className="flex w-full flex-col gap-1">
            {/* 
          ToDo:
          *  The label is not actually connected to the input
        */}
            <label className="text-sm" htmlFor="address">
              Sök på adress
            </label>
            <GoogleAddressInput
              defaultValue={renderProps.field.value?.name}
              onBlur={renderProps.field.onBlur}
              className="border"
              onAddressSelected={(address) => {
                renderProps.field.onChange({
                  name: address.formattedAddress,
                  ...address,
                });
              }}
            />
            <div className="h-6">
              {!address && (
                <p className="text-sm text-dark-gray">
                  Klicka på adressen i dropdownen för att välja adress.
                </p>
              )}
              {!!address && (
                <div className="mr-auto inline-flex rounded-full bg-stone-600 px-4 py-1 text-xs text-light-background">
                  {address}
                </div>
              )}
            </div>
          </div>
        );
      }}
    />
  );
}

function DurationFieldset(props: ChildComponentProps) {
  return (
    <section className="flex flex-col gap-2">
      <AppFormTextField
        register={props.form.register}
        name="timeEstimateInMinutes"
        label="Tidsåtgång i minuter"
        htmlAttributes={{
          type: "number",
          placeholder: "Ange tidsåtgång i minuter",
        }}
      />
      <section className="flex gap-2">
        <AppButton
          onClick={() => {
            props.form.setValue("timeEstimateInMinutes", 120);
          }}
          variant="outline-default"
          contentClassName="px-2 py-1 text-sm"
        >
          Planerat arbete (2h)
        </AppButton>
        <AppButton
          onClick={() => {
            props.form.setValue("timeEstimateInMinutes", 180);
          }}
          variant="outline-default"
          contentClassName="px-2 py-1 text-sm"
        >
          Lätt arbete (3h)
        </AppButton>
        <AppButton
          onClick={() => {
            props.form.setValue("timeEstimateInMinutes", 240);
          }}
          variant="outline-default"
          contentClassName="px-2 py-1 text-sm"
        >
          Svårt arbete (4h)
        </AppButton>
      </section>
    </section>
  );
}

function DaySelectFieldset(props: ChildComponentProps) {
  return (
    <Controller
      control={props.form.control}
      name="weekDays"
      render={(renderProps) => {
        const current = renderProps.field.value ?? [];

        return (
          <>
            <ul className="flex">
              {props.dates.days.map((dateObject, i) => {
                const disabled = !dateObject.selectable;

                const isSelected = current[i];
                const activeUnselected = !isSelected && !disabled;

                function onClick() {
                  if (disabled) {
                    return;
                  }

                  renderProps.field.onChange([
                    ...current.slice(0, i),
                    !isSelected,
                    ...current.slice(i + 1),
                  ]);
                }

                return (
                  <li
                    key={dateObject.date.getTime()}
                    className="flex grow flex-col"
                  >
                    <button
                      onClick={onClick}
                      type="button"
                      className={ClientUtils.twClassNames(
                        "border-y border-l border-gray-300 p-2 text-center text-dark-gray transition-all last:border-r",
                        !disabled && "cursor-pointer",
                        activeUnselected &&
                        "bg-brand-blue-100 hover:bg-brand-blue-200",
                        isSelected && "bg-brand-blue-200 font-bold",
                        disabled && "pointer-events-none opacity-50"
                      )}
                    >
                      {dateObject.dayOfWeek}
                    </button>
                  </li>
                );
              })}
            </ul>
            {!!renderProps.fieldState.error && (
              <span className="h-8 text-sm text-red-600">
                Välj minst en veckodag
              </span>
            )}
          </>
        );
      }}
    />
  );
}

export default SalesTeamBookingSuggestionsForm;
